import { supabase } from "@/lib/supabase";

type ProgressResponse = { payload: string | null; savedAt?: string };

export type PersistAttemptInput = {
  studentName: string;
  studentEmail?: string;
  classroomName: string;
  classroomId?: string | null;
  remainingSeconds: number;
  answers: Record<number, string>;
  questions: Array<{
    id: string;
    numero: number;
    correta: "A" | "B" | "C" | "D";
  }>;
};

export type RemoteAttempt = {
  id: string;
  student_id: string;
  student_name: string | null;
  student_email: string | null;
  classroom_id: string | null;
  classroom_name: string | null;
  attempt_number: number;
  started_at: string | null;
  finished_at: string;
  correct: number;
  answered: number;
  percentage: number;
  remaining_seconds: number;
  answers: Array<{
    question_id: string;
    display_number: number;
    selected_answer: string | null;
    correct_answer: string;
    is_correct: boolean;
  }>;
};

async function requireUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) throw error;
  if (!user) {
    throw new Error("É necessário entrar na conta para sincronizar.");
  }

  return user;
}

export async function loadRemoteProgress(): Promise<ProgressResponse> {
  const user = await requireUser();

  const { data, error } = await supabase
    .from("student_progress")
    .select("payload,saved_at")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error) {
    throw new Error("Não foi possível acessar o progresso sincronizado.");
  }

  return {
    payload: typeof data?.payload === "string" ? data.payload : null,
    savedAt: typeof data?.saved_at === "string" ? data.saved_at : undefined,
  };
}

export async function saveRemoteProgress(payload: string): Promise<ProgressResponse> {
  const user = await requireUser();

  const { data, error } = await supabase
    .from("student_progress")
    .upsert(
      {
        user_id: user.id,
        payload,
        saved_at: new Date().toISOString(),
        version: 1,
      },
      { onConflict: "user_id" },
    )
    .select("payload,saved_at")
    .single();

  if (error || !data) {
    throw new Error("Não foi possível salvar o progresso sincronizado.");
  }

  return {
    payload: data.payload,
    savedAt: data.saved_at,
  };
}

export async function loadRemoteAttempts(scope: "own" | "all" = "own"): Promise<RemoteAttempt[]> {
  const user = await requireUser();

  const { data: attemptRows, error: attemptError } = await supabase
    .from("attempts")
    .select("id,student_id,student_name,student_email,classroom_id,classroom_name,attempt_number,started_at,finished_at,correct,answered,percentage,remaining_seconds")
    .order("finished_at", { ascending: false });

  if (attemptError) {
    throw new Error("Não foi possível carregar os resultados centralizados.");
  }

  const visibleAttempts = (attemptRows ?? []).filter((row) => scope === "all" || row.student_id === user.id);

  if (!visibleAttempts.length) return [];

  const ids = visibleAttempts.map((row) => row.id);
  const { data: answerRows, error: answerError } = await supabase
    .from("attempt_answers")
    .select("attempt_id,question_id,display_number,selected_answer,correct_answer,is_correct")
    .in("attempt_id", ids);

  // As tentativas devem continuar visíveis mesmo se alguma resposta individual
  // não puder ser carregada. Assim, o painel docente não perde o aluno/resultado
  // inteiro por uma falha de leitura das respostas.
  const answersByAttempt = new Map<string, RemoteAttempt["answers"]>();
  if (answerError) {
    console.warn("Não foi possível carregar as respostas detalhadas das tentativas:", answerError);
  }
  for (const row of answerRows ?? []) {
    const list = answersByAttempt.get(row.attempt_id) ?? [];
    list.push({
      question_id: row.question_id,
      display_number: row.display_number,
      selected_answer: row.selected_answer,
      correct_answer: row.correct_answer,
      is_correct: row.is_correct,
    });
    answersByAttempt.set(row.attempt_id, list);
  }

  return visibleAttempts.map((row) => ({
    id: row.id,
    student_id: row.student_id,
    student_name: row.student_name,
    student_email: row.student_email,
    classroom_id: row.classroom_id,
    classroom_name: row.classroom_name,
    attempt_number: row.attempt_number,
    started_at: row.started_at,
    finished_at: row.finished_at,
    correct: row.correct,
    answered: row.answered,
    percentage: row.percentage,
    remaining_seconds: row.remaining_seconds,
    answers: answersByAttempt.get(row.id) ?? [],
  }));
}

export async function saveRemoteAttempt(input: PersistAttemptInput) {
  const user = await requireUser();

  const answerPayload = input.questions.map((question, index) => ({
    question_id: question.id,
    display_number: index + 1,
    selected_answer: input.answers[question.numero] ?? null,
    correct_answer: question.correta,
  }));

  const { data, error } = await supabase.functions.invoke("submit-attempt-v2", {
    body: {
      student_name: input.studentName.trim() || "Estudante",
      student_email: input.studentEmail?.trim() || user.email || null,
      classroom_id: input.classroomId || null,
      classroom_name: input.classroomName || null,
      remaining_seconds: input.remainingSeconds,
      answers: answerPayload,
    },
  });

  if (error || !data?.id) {
    throw new Error(error?.message || data?.error || "Não foi possível registrar a tentativa no servidor.");
  }

  return data.id as string;
}
